import type { RompArea, Rompimento } from '../types';
import { fmtDateTime } from './occurrences';

/** Normaliza as áreas (suporta registros antigos com centro+raioMetros). */
export function getAreas(r: Pick<Rompimento, 'areas' | 'centro' | 'raioMetros'>): RompArea[] {
  if (Array.isArray(r.areas) && r.areas.length) return r.areas;
  if (r.centro && typeof r.centro.lat === 'number' && typeof r.centro.lng === 'number') {
    return [{ tipo: 'circulo', lat: r.centro.lat, lng: r.centro.lng, raio: r.raioMetros || 300 }];
  }
  return [];
}

export function fmtRaio(m?: number | null): string {
  if (m == null) return '—';
  return m >= 1000 ? (m / 1000).toFixed(2).replace('.', ',') + ' km' : Math.round(m) + ' m';
}

export function areaLabel(r: Rompimento): string {
  const areas = getAreas(r);
  if (!areas.length) return 'Área não definida';
  if (areas.length > 1) return `${areas.length} áreas afetadas`;
  const a = areas[0];
  if (a.tipo === 'circulo') return `Raio ${fmtRaio(a.raio)}`;
  return a.nome ? `Bairro ${a.nome}` : 'Área desenhada';
}

export interface RompStatus {
  cls: 'agendado' | 'ativo' | 'encerrado';
  label: string;
}

export function rompStatus(r: Rompimento): RompStatus {
  const now = Date.now();
  const ini = r.inicio ? new Date(r.inicio).getTime() : null;
  const fim = r.fim ? new Date(r.fim).getTime() : null;
  if (ini && now < ini) return { cls: 'agendado', label: 'Agendado' };
  if (fim == null) return { cls: 'ativo', label: 'Em andamento' };
  if (now > fim) return { cls: 'encerrado', label: 'Encerrado' };
  return { cls: 'ativo', label: 'Ativo' };
}

export function isEmAndamento(r: Rompimento): boolean {
  if (r.fim) return false;
  const ini = r.inicio ? new Date(r.inicio).getTime() : null;
  return ini == null || ini <= Date.now();
}

/** Mensagem pronta para clientes: usa a digitada ou gera automaticamente. */
export function rompMensagem(r: Rompimento): string {
  if (r.mensagem && r.mensagem.trim()) return r.mensagem.trim();
  const linhas = [
    '⚠️ *COMUNICADO — ROMPIMENTO DE FIBRA*',
    r.titulo ? `*${r.titulo}*` : null,
    '',
    (r.cidades || []).length ? `🏙 Cidades: ${r.cidades.join(', ')}` : null,
    (r.bairros || []).length ? `📍 Bairros afetados: ${r.bairros.join(', ')}` : null,
    `🕒 Início: ${fmtDateTime(r.inicio)}`,
    `🔧 Previsão de normalização: ${r.previsao ? fmtDateTime(r.previsao) : 'em apuração'}`,
    '',
    'Nossas equipes já estão atuando na correção. Assim que o sinal for restabelecido, avisaremos por aqui. Pedimos desculpas pelo transtorno.'
  ];
  return linhas.filter((l) => l !== null).join('\n');
}

export function polyCentroid(pts: { lat: number; lng: number }[]): { lat: number; lng: number } {
  let lat = 0;
  let lng = 0;
  pts.forEach((p) => {
    lat += p.lat;
    lng += p.lng;
  });
  return { lat: lat / pts.length, lng: lng / pts.length };
}

/** Reduz polígonos muito detalhados para um limite de pontos. */
export function simplifyPts<T>(pts: T[], max = 150): T[] {
  if (pts.length <= max) return pts;
  const step = pts.length / max;
  const out: T[] = [];
  for (let i = 0; i < pts.length; i += step) out.push(pts[Math.floor(i)]);
  return out;
}

/** Date → valor de <input type="datetime-local"> (YYYY-MM-DDTHH:mm) no fuso local. */
export function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** Copia texto para a área de transferência com fallback. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}
